var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import "./table.css";
import { useNavigate } from "react-router";
import Button from "../../atoms/button/button";
import Shimmer from "../../atoms/shimmer/shimmer";
import Input from "../../atoms/input/input";
var EMPTY_SELECTED_DATA = [];
var TableCard = function (props) {
    var _a;
    var _b = useState(1), currentPage = _b[0], setCurrentPage = _b[1];
    var _c = useState([]), chips = _c[0], setChips = _c[1];
    var _d = useState(""), rawInput = _d[0], setRawInput = _d[1];
    var _e = useState(null), pendingColumn = _e[0], setPendingColumn = _e[1];
    var _f = useState(false), showDropdown = _f[0], setShowDropdown = _f[1];
    var _g = useState([]), rows = _g[0], setRows = _g[1];
    var searchRef = useRef(null);
    var inputRef = useRef(null);
    var handleSelectedData = props.handleSelectedData;
    // `props.selectData` is the single source of truth for the current
    // selection (the parent owns it, e.g. to clear it after a delete
    // completes) - keeping a separate local copy here let them drift apart:
    // a parent-side reset wouldn't reach a copy that only ever seeded itself
    // once on mount.
    var selectedData = (_a = props.selectData) !== null && _a !== void 0 ? _a : EMPTY_SELECTED_DATA;
    var navigate = useNavigate();
    var totalRows = props.data.length;
    var filterableHeaders = props.headers
        .map(function (h, i) { return ({ column: h, columnIndex: i }); })
        .filter(function (h) { return h.column !== "" && h.columnIndex !== props.headers.length - 1; });
    var availableHeaders = filterableHeaders.filter(function (h) { return !chips.some(function (c) { return c.columnIndex === h.columnIndex; }); });
    var getCellString = function (cell) {
        var _a;
        if (cell == null)
            return "";
        if (typeof cell === "boolean")
            return "";
        if (typeof cell === "string")
            return cell;
        if (typeof cell === "object")
            return (_a = cell === null || cell === void 0 ? void 0 : cell.text) !== null && _a !== void 0 ? _a : "";
        return String(cell);
    };
    var filteredRows = props.data.filter(function (row) {
        var chipsMatch = chips.every(function (chip) {
            var cellStr = getCellString(row[chip.columnIndex]);
            return cellStr.toLowerCase().includes(chip.value.toLowerCase());
        });
        var globalMatch = rawInput === "" ||
            row.some(function (cell) {
                return getCellString(cell).toLowerCase().includes(rawInput.toLowerCase());
            });
        return chipsMatch && globalMatch;
    });
    var totalFilteredRows = filteredRows.length;
    var totalPages = Math.ceil(totalFilteredRows / props.rowsPerPage);
    var startIndex = (currentPage - 1) * props.rowsPerPage;
    var endIndex = startIndex + props.rowsPerPage;
    var endEntry = Math.min(currentPage * props.rowsPerPage, totalFilteredRows);
    useEffect(function () {
        handleSelectedData === null || handleSelectedData === void 0 ? void 0 : handleSelectedData([]);
    }, [props.currentTab]);
    useEffect(function () {
        var currentRows = filteredRows
            .slice(startIndex, endIndex)
            .map(function (row) {
            return props.multiSelect
                ? __spreadArray([selectedData.includes(row[1])], row.slice(1), true) : row;
        });
        setRows(currentRows);
    }, [
        totalFilteredRows,
        currentPage,
        chips,
        rawInput,
        props.data,
        selectedData,
    ]);
    // Close dropdown on outside click
    useEffect(function () {
        var handleClickOutside = function (e) {
            if (searchRef.current && !searchRef.current.contains(e.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return function () { return document.removeEventListener("mousedown", handleClickOutside); };
    }, []);
    var emptyRows = Array.from({ length: Math.max(0, props.rowsPerPage - rows.length) }, function (_, index) { return index; });
    var handlePageChange = function (page) { return setCurrentPage(page); };
    var handleRowClick = function (link) {
        if (link) {
            if (typeof link === "string") {
                navigate(link);
            }
            else {
                navigate(link.link, link.state && link.state);
            }
        }
    };
    var generatePageNumbers = function () {
        var maxPageButtons = 5;
        var pageNumbers = [];
        var startPage = Math.max(1, Math.min(currentPage - Math.floor(maxPageButtons / 2), totalPages - maxPageButtons + 1));
        var endPage = Math.min(totalPages, startPage + maxPageButtons - 1);
        for (var i = startPage; i <= endPage; i++)
            pageNumbers.push(i);
        return pageNumbers;
    };
    var formatNumber = function (num) { return (num < 10 ? "0".concat(num) : num.toString()); };
    var handleInputKeyDown = function (e) {
        if (e.key === "Enter" && rawInput.trim() !== "") {
            if (pendingColumn) {
                setChips(function (prev) { return __spreadArray(__spreadArray([], prev, true), [
                    {
                        column: pendingColumn.column,
                        columnIndex: pendingColumn.columnIndex,
                        value: rawInput.trim(),
                    },
                ], false); });
                setPendingColumn(null);
                setRawInput("");
                setCurrentPage(1);
            }
            setShowDropdown(false);
        }
        if (e.key === "Backspace" && rawInput === "") {
            if (pendingColumn) {
                // Cancel column selection
                setPendingColumn(null);
                setShowDropdown(true);
            }
            else if (chips.length > 0) {
                setChips(function (prev) { return prev.slice(0, -1); });
                setCurrentPage(1);
            }
        }
        if (e.key === "Escape") {
            setShowDropdown(false);
            setPendingColumn(null);
            setRawInput("");
        }
    };
    var selectColumn = function (header) {
        var _a;
        setPendingColumn(header);
        setRawInput("");
        setShowDropdown(false);
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    };
    var removeChip = function (index) {
        setChips(function (prev) { return prev.filter(function (_, i) { return i !== index; }); });
        setCurrentPage(1);
    };
    var clearAll = function () {
        setChips([]);
        setRawInput("");
        setPendingColumn(null);
        setCurrentPage(1);
    };
    var handleInputFocus = function () {
        if (!pendingColumn)
            setShowDropdown(true);
    };
    var selectAllRows = function () {
        var updatedData = isSelectedAll ? [] : filteredRows.map(function (row) { return row[1]; });
        handleSelectedData === null || handleSelectedData === void 0 ? void 0 : handleSelectedData(updatedData);
    };
    var radioColumns = {
        "Questionnaire Attempts": ["0 / 2", "1 / 2", "2 / 2", "1 / 3", "2 / 3", "3 / 3"],
        "Employment Status": ["No", "Yes"],
    };
    var handleChange = function (e) {
        var value = e.target.value;
        var isValuePresent = selectedData.length > 0 && selectedData.includes(value);
        var updatedVal = isValuePresent
            ? selectedData.filter(function (entity) { return entity !== value; })
            : __spreadArray(__spreadArray([], selectedData, true), [value], false);
        handleSelectedData === null || handleSelectedData === void 0 ? void 0 : handleSelectedData(updatedVal);
    };
    var isSelectedAll = filteredRows.length > 0 &&
        filteredRows.every(function (row) { return selectedData.includes(row[1]); });
    var hasActiveFilters = chips.length > 0 || rawInput !== "";
    return (_jsxs("div", __assign({ className: "custom-table-container" }, { children: [props.showSearchBar && (_jsxs("div", __assign({ className: "filter-search-wrapper", ref: searchRef }, { children: [_jsxs("div", __assign({ className: "filter-search-bar ".concat(showDropdown ? "focused" : ""), onClick: function () { var _a; return (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus(); } }, { children: [_jsxs("svg", __assign({ className: "filter-search-icon", width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg" }, { children: [_jsx("circle", { cx: "11", cy: "11", r: "8", stroke: "currentColor", strokeWidth: "2" }), _jsx("path", { d: "M21 21L16.65 16.65", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round" })] })), chips.map(function (chip, i) { return (_jsxs("span", __assign({ className: "filter-chip" }, { children: [_jsx("span", __assign({ className: "filter-chip-column" }, { children: chip.column })), _jsx("span", __assign({ className: "filter-chip-separator" }, { children: ":" })), _jsx("span", __assign({ className: "filter-chip-value" }, { children: chip.value })), _jsx("button", __assign({ className: "filter-chip-remove", onClick: function (e) {
                                            e.stopPropagation();
                                            removeChip(i);
                                        } }, { children: "\u00D7" }))] }), i)); }), pendingColumn && (_jsxs("span", __assign({ className: "filter-chip filter-chip--pending" }, { children: [_jsx("span", __assign({ className: "filter-chip-column" }, { children: pendingColumn.column })), _jsx("span", __assign({ className: "filter-chip-separator" }, { children: ":" }))] }))), pendingColumn ? (radioColumns[pendingColumn.column] ? (_jsx("div", __assign({ className: "filter-radio-group" }, { children: radioColumns[pendingColumn.column].map(function (option) { return (_jsx("button", __assign({ className: "filter-radio-option", onMouseDown: function (e) {
                                        e.preventDefault();
                                        setChips(function (prev) { return __spreadArray(__spreadArray([], prev, true), [
                                            {
                                                column: pendingColumn.column,
                                                columnIndex: pendingColumn.columnIndex,
                                                value: option,
                                            },
                                        ], false); });
                                        setPendingColumn(null);
                                        setRawInput("");
                                        setShowDropdown(false);
                                        setCurrentPage(1);
                                    } }, { children: option }), option)); }) }))) : (_jsx("input", { ref: inputRef, className: "filter-search-input", placeholder: "Filter by ".concat(pendingColumn.column, "..."), value: rawInput, onChange: function (e) {
                                    setRawInput(e.target.value);
                                    setCurrentPage(1);
                                }, onFocus: handleInputFocus, onKeyDown: handleInputKeyDown }))) : (_jsx("input", { ref: inputRef, className: "filter-search-input", placeholder: chips.length === 0 ? "Search or filter..." : "Add filter...", value: rawInput, onChange: function (e) {
                                    setRawInput(e.target.value);
                                    setCurrentPage(1);
                                }, onFocus: handleInputFocus, onKeyDown: handleInputKeyDown })), hasActiveFilters && (_jsx("button", __assign({ className: "filter-clear-btn", onClick: clearAll }, { children: "\u00D7" })))] })), showDropdown && availableHeaders.length > 0 && (_jsxs("div", __assign({ className: "filter-dropdown" }, { children: [_jsx("p", __assign({ className: "filter-dropdown-label" }, { children: "Filter by column" })), availableHeaders.map(function (h) { return (_jsx("button", __assign({ className: "filter-dropdown-item", onMouseDown: function (e) {
                                    e.preventDefault();
                                    selectColumn(h);
                                } }, { children: h.column }), h.columnIndex)); }), _jsx("div", { className: "filter-dropdown-divider" }), _jsxs("p", __assign({ className: "filter-dropdown-hint" }, { children: ["Press ", _jsx("kbd", { children: "Enter" }), " to search all columns"] }))] })))] }))), _jsx("div", __assign({ className: "card-background" }, { children: _jsxs("table", __assign({ className: "custom-table" }, { children: [_jsx("thead", { children: _jsx("tr", __assign({ className: "custom-table-header table-header" }, { children: props.headers.map(function (header, index) {
                                    var _a, _b;
                                    return (_jsx("th", __assign({ className: "custom-table-th align-".concat(index === props.headers.length - 1 ? "right" : "left"), onClick: function () {
                                            if (header &&
                                                index !== props.headers.length - 1 &&
                                                props.onSort) {
                                                props.onSort(index);
                                            }
                                        }, style: {
                                            cursor: header &&
                                                index !== props.headers.length - 1 &&
                                                props.onSort
                                                ? "pointer"
                                                : "default",
                                            userSelect: "none",
                                        } }, { children: index === 0 && props.multiSelect ? (_jsx(Input, { type: "checkbox", className: "select-input", name: "list", onChange: selectAllRows, onInputChange: function () { }, isChecked: isSelectedAll })) : (_jsxs("span", __assign({ className: "flex items-center gap-1" }, { children: [header, header &&
                                                    index !== props.headers.length - 1 &&
                                                    props.onSort && (_jsx("span", __assign({ style: {
                                                        opacity: ((_a = props.sortConfig) === null || _a === void 0 ? void 0 : _a.column) === index ? 1 : 0.3,
                                                        fontSize: "0.75rem",
                                                    } }, { children: ((_b = props.sortConfig) === null || _b === void 0 ? void 0 : _b.column) === index &&
                                                        props.sortConfig.direction === "desc"
                                                        ? "\u25BC"
                                                        : "\u25B2" })))] }))) }), index));
                                }) })) }), _jsxs("tbody", { children: [rows &&
                                    rows.map(function (row, rowIndex) {
                                        var hasLink = row.some(function (cell) { return typeof cell === "object"; });
                                        var hasMultipleLinks = row.filter(function (cell) { return typeof cell === "object"; }).length > 1;
                                        return (_jsx("tr", __assign({ className: "custom-table-row ".concat(hasMultipleLinks
                                                ? ""
                                                : props.multiSelect
                                                    ? ""
                                                    : hasLink
                                                        ? "with-link"
                                                        : "", " ").concat(props.multiSelect ? "" : hasLink ? "clickable-row" : ""), onClick: function () {
                                                if (!props.multiSelect && !hasMultipleLinks && hasLink) {
                                                    handleRowClick(row.find(function (cell) { return typeof cell === "object"; }));
                                                }
                                            } }, { children: row.map(function (cell, cellIndex) { return (_jsx("td", __assign({ className: "custom-table-td align-".concat(cellIndex === row.length - 1 ? "right" : "left") }, { children: cell == null ? (_jsx(Shimmer, { className: "w-full h-4 rounded-3xl my-4" })) : typeof cell === "string" ? (cell) : typeof cell === "boolean" ? (_jsx(Input, { type: "checkbox", className: "select-input", name: rowIndex, value: "".concat(row[1]), onChange: handleChange, onInputChange: function () { }, isChecked: cell })) : cell && cell.type === "button" ? (_jsx(Button, __assign({ onClick: function () {
                                                        return navigate(cell.link ? cell.link : "", cell.state && cell.state);
                                                    }, className: "adviser-button ".concat(cell.classname ? cell.classname : "") }, { children: cell.text }))) : cell.type === "toggle" ? (_jsx(Button, __assign({ onClick: function () { var _a; return (_a = props.handleClick) === null || _a === void 0 ? void 0 : _a.call(props, cell); }, className: "adviser-button ".concat(cell.classname ? cell.classname : "") }, { children: cell.text }))) : cell.type === "tooltip" ? (_jsxs("div", __assign({ className: "tooltip-container" }, { children: [_jsx("span", __assign({ className: "text-red-500" }, { children: cell.text })), _jsx("span", __assign({ className: "tooltip-text" }, { children: cell.tooltip }))] }))) : (_jsx("span", __assign({ onClick: function () {
                                                        return navigate(cell.link ? cell.link : "", cell.state && cell.state);
                                                    }, style: { cursor: "pointer", whiteSpace: "nowrap" } }, { children: cell.text }))) }), cellIndex)); }) }), rowIndex));
                                    }), emptyRows.map(function (index) { return (_jsxs("tr", { children: [props.headers.map(function (_, cellIndex) { return (_jsx("td", {}, cellIndex)); }), props.headers.length !== 0 &&
                                            props.data[0] &&
                                            props.headers.length < props.data[0].length &&
                                            Array.from({ length: props.data[0].length - props.headers.length }, function (_, i) { return (_jsx("td", {}, "empty-cell-".concat(i + props.headers.length))); })] }, "empty-".concat(index))); })] })] })) })), !props.hidePagination && (_jsxs("div", __assign({ className: "custom-table-pagination pagination-card" }, { children: [_jsx("div", __assign({ className: "custom-table-pagination-info pagination-info" }, { children: "Showing ".concat(startIndex + 1, " to ").concat(endEntry, " of ").concat(totalRows, " entries") })), _jsxs("div", __assign({ className: "custom-table-pagination-controls pagination-controls" }, { children: [_jsx("button", __assign({ className: "custom-table-pagination-button", onClick: function () { return handlePageChange(currentPage - 1); }, disabled: currentPage === 1 }, { children: "<" })), _jsx("div", __assign({ className: "custom-table-page-numbers page-numbers" }, { children: generatePageNumbers().map(function (pageNumber) { return (_jsx("span", __assign({ className: pageNumber === currentPage ? "current-page" : "", onClick: function () { return handlePageChange(pageNumber); } }, { children: formatNumber(pageNumber) }), pageNumber)); }) })), _jsx("button", __assign({ onClick: function () { return handlePageChange(currentPage + 1); }, disabled: currentPage === totalPages }, { children: ">" }))] }))] })))] })));
};
TableCard.propTypes = {
    headers: PropTypes.arrayOf(PropTypes.string).isRequired,
    data: PropTypes.arrayOf(PropTypes.array).isRequired,
    rowsPerPage: PropTypes.number.isRequired,
};
export default TableCard;
